if (!customElements.get('product-magnifier')) {
  customElements.define('product-magnifier',
    class ProductMagnifier extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.sectionId    = this.dataset.sectionId;
        this.sectionIndex = this.dataset.sectionIndex;

        this.mainSwiperSel  = '.swiper-' + this.sectionId;
        this.thumbSwiperSel = '.swiper-thumbs-' + this.sectionIndex;
        this.swiperKey      = 'swiperGeneral' + this.sectionIndex;

        this.thumbsContainer = this.querySelector('.product-magnifier__thumbs');
        this.imageContainer  = this.querySelector('.product-magnifier__image-container');
        this.imageEl         = this.querySelector('.product-magnifier__image');
        this.videoEl         = this.querySelector('.product-magnifier__video');
        this.videoSrcEl      = this.querySelector('.product-magnifier__video source');
        this.playBtnEl       = this.querySelector('.product-magnifier__play-btn');

        this.slides       = [];
        this.currentIndex = 0;
        this.isZoomed     = false;
        this.zoomScale    = 2.5;
        this._videoPaused = true;

        this._touchStartX = 0;
        this._touchStartY = 0;

        this._isPinching        = false;
        this._pinchStartDist    = 0;
        this._pinchStartScale   = 1;
        this._pinchCurrentScale = 1;
        this._pinchOriginX      = 50;
        this._pinchOriginY      = 50;

        this._lastTapTime      = 0;
        this._lastTapX         = 0;
        this._lastTapY         = 0;
        this._lastTouchEndTime = 0;

        // Tracks the live transformOrigin so delta-based touch pan can build on it
        this._currentOriginX = 50;
        this._currentOriginY = 50;
        this._panLastX       = 0;
        this._panLastY       = 0;

        this._bindEvents();
        this._hookGalleryClicks();
        this._hookSlidesUpdated();
      }

      _bindEvents() {
        this.querySelector('.product-magnifier__close')
          .addEventListener('click', () => this.close());

        this.addEventListener('click', (e) => {
          if (e.target === this) this.close();
        });

        document.addEventListener('keydown', (e) => {
          if (!this.isOpen()) return;
          if (e.key === 'Escape')     { this.close(); return; }
          if (e.key === 'ArrowRight') { this.goTo(this.currentIndex + 1); return; }
          if (e.key === 'ArrowLeft')  { this.goTo(this.currentIndex - 1); return; }
        });

        this.imageContainer.addEventListener('click', (e) => this._onContainerClick(e));
        this.imageContainer.addEventListener('mousemove', (e) => this._onMouseMove(e));

        this.playBtnEl.addEventListener('click', (e) => {
          e.stopPropagation();
          this._toggleVideo();
        });

        this.addEventListener('touchstart', (e) => this._onTouchStart(e), { passive: false });
        this.addEventListener('touchmove',  (e) => this._onTouchMove(e),  { passive: false });
        this.addEventListener('touchend',   (e) => this._onTouchEnd(e),   { passive: false });
      }

      _hookGalleryClicks() {
        const mainSwiper = document.querySelector(this.mainSwiperSel);
        if (!mainSwiper) return;

        mainSwiper.addEventListener('click', (e) => {
          if (e.target.closest('product-magnifier')) return;

          const slide = e.target.closest('.swiper-slide:not(.swiper-slide-duplicate)');
          if (!slide) return;

          if (slide.querySelector('video')) return;

          const sw  = window.sectionSwipers && window.sectionSwipers[this.swiperKey];
          const idx = sw ? sw.realIndex : 0;

          this._syncFromDOM();
          this.open(idx);
        });
      }

      _hookSlidesUpdated() {
        document.addEventListener('product:magnifier-slides-updated', () => {
          if (!this.isOpen()) return;
          this._syncFromDOM();
          this._renderThumbs();
          this.goTo(0);
        });
      }

      _syncFromDOM() {
        this.slides = [];

        const mainSlides  = document.querySelectorAll(this.mainSwiperSel  + ' .swiper-slide:not(.swiper-slide-duplicate)');
        const thumbSlides = document.querySelectorAll(this.thumbSwiperSel + ' .swiper-slide:not(.swiper-slide-duplicate)');

        mainSlides.forEach((slide, i) => {
          const img      = slide.querySelector('img');
          const video    = slide.querySelector('video');
          const thumbImg = thumbSlides[i] ? thumbSlides[i].querySelector('img') : null;
          const thumbSrc = thumbImg ? (thumbImg.currentSrc || thumbImg.getAttribute('src') || '') : '';

          if (video) {
            const src = video.querySelector('source');
            this.slides.push({
              type:     'video',
              videoSrc: src ? src.getAttribute('src') : '',
              poster:   video.getAttribute('poster') || '',
              thumbSrc: thumbSrc || video.getAttribute('poster') || '',
            });
          } else if (img) {
            this.slides.push({
              type:     'image',
              src:      this._highResSrc(img),
              alt:      img.alt || '',
              thumbSrc: thumbSrc || img.getAttribute('src') || '',
            });
          }
        });
      }

      _highResSrc(img) {
        const srcset = img.getAttribute('srcset');
        let base = img.currentSrc || img.getAttribute('src') || '';

        if (srcset) {
          const last = srcset.split(',').map((s) => s.trim()).filter(Boolean).pop();
          if (last) base = last.split(/\s+/)[0] || base;
        }

        return base.replace(/([?&]width=)\d+/, '$12800');
      }

      open(index) {
        if (!this.slides.length) return;
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this._renderThumbs();
        this.goTo(index != null ? index : 0);
      }

      close() {
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        this._resetZoom();
        this.videoEl.pause();
        this._videoPaused = true;
        this.playBtnEl.classList.add('is-paused');
      }

      isOpen() {
        return this.classList.contains('is-open');
      }

      goTo(index) {
        const len = this.slides.length;
        if (!len) return;
        this.currentIndex = ((index % len) + len) % len;
        this._resetZoom();
        this._showSlide(this.currentIndex);
        this._setActiveThumb(this.currentIndex);
        this._scrollThumbIntoView(this.currentIndex);
      }

      _showSlide(index) {
        const slide = this.slides[index];
        if (!slide) return;

        if (slide.type === 'video') {
          this.imageEl.style.display    = 'none';
          this.videoSrcEl.setAttribute('src', slide.videoSrc);
          this.videoEl.setAttribute('poster', slide.poster);
          this.videoEl.style.display    = 'block';
          this.playBtnEl.style.display  = 'flex';
          this.imageContainer.classList.add('video-active');
          this.videoEl.load();
          this.videoEl.muted = true;
          this.videoEl.play().then(() => {
            this._videoPaused = false;
            this.playBtnEl.classList.remove('is-paused');
          }).catch(() => {
            this._videoPaused = true;
            this.playBtnEl.classList.add('is-paused');
          });
        } else {
          this.videoEl.pause();
          this.videoEl.style.display   = 'none';
          this.playBtnEl.style.display = 'none';
          this.playBtnEl.classList.add('is-paused');
          this.imageContainer.classList.remove('video-active');
          this.imageEl.style.display = 'block';
          this.imageEl.setAttribute('src', slide.src);
          this.imageEl.setAttribute('alt', slide.alt);
        }
      }

      _renderThumbs() {
        this.thumbsContainer.innerHTML = '';
        this.slides.forEach((slide, i) => {
          const btn = document.createElement('button');
          btn.type      = 'button';
          btn.className = 'product-magnifier__thumb' + (i === this.currentIndex ? ' is-active' : '');
          btn.setAttribute('aria-label', 'View image ' + (i + 1));
          btn.setAttribute('aria-pressed', String(i === this.currentIndex));

          const img = document.createElement('img');
          img.src     = slide.thumbSrc;
          img.alt     = slide.alt || '';
          img.loading = 'lazy';
          btn.appendChild(img);

          btn.addEventListener('click', () => this.goTo(i));
          this.thumbsContainer.appendChild(btn);
        });
      }

      _setActiveThumb(index) {
        this.thumbsContainer.querySelectorAll('.product-magnifier__thumb').forEach((t, i) => {
          t.classList.toggle('is-active', i === index);
          t.setAttribute('aria-pressed', String(i === index));
        });
      }

      _scrollThumbIntoView(index) {
        const thumb = this.thumbsContainer.children[index];
        if (thumb) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }

      _onContainerClick(e) {
        if (Date.now() - this._lastTouchEndTime < 500) return;
        if (this.imageContainer.classList.contains('video-active')) {
          this._toggleVideo();
          return;
        }

        if (this.isZoomed) {
          this._resetZoom();
        } else {
          const rect = this.imageContainer.getBoundingClientRect();
          const x    = ((e.clientX - rect.left) / rect.width)  * 100;
          const y    = ((e.clientY - rect.top)  / rect.height) * 100;
          this._zoomTo(x, y, true);
        }
      }

      _onMouseMove(e) {
        if (!this.isZoomed || this.imageContainer.classList.contains('video-active')) return;
        const rect = this.imageContainer.getBoundingClientRect();
        const x    = ((e.clientX - rect.left) / rect.width)  * 100;
        const y    = ((e.clientY - rect.top)  / rect.height) * 100;
        this._setZoomOrigin(x, y, false);
      }

      _zoomTo(x, y, animate) {
        this.isZoomed = true;
        this.imageContainer.classList.add('is-zoomed');
        this._setZoomOrigin(x, y, false);
        if (!animate) this.imageEl.classList.add('pan-active');
        this.imageEl.style.transform = 'scale(' + this.zoomScale + ')';
        if (!animate) {
          requestAnimationFrame(() => this.imageEl.classList.remove('pan-active'));
        }
      }

      _setZoomOrigin(x, y, animate) {
        this._currentOriginX = x;
        this._currentOriginY = y;
        if (!animate) this.imageEl.classList.add('pan-active');
        this.imageEl.style.transformOrigin = x + '% ' + y + '%';
        if (!animate) {
          requestAnimationFrame(() => this.imageEl.classList.remove('pan-active'));
        }
      }

      _resetZoom() {
        this.isZoomed        = false;
        this.zoomScale       = 2.5;
        this._currentOriginX = 50;
        this._currentOriginY = 50;
        this.imageContainer.classList.remove('is-zoomed');
        this.imageEl.classList.remove('pan-active');
        this.imageEl.style.transform       = '';
        this.imageEl.style.transformOrigin = 'center center';
      }

      _toggleVideo() {
        if (this._videoPaused) {
          this.videoEl.play().catch(() => {});
          this._videoPaused = false;
          this.playBtnEl.classList.remove('is-paused');
        } else {
          this.videoEl.pause();
          this._videoPaused = true;
          this.playBtnEl.classList.add('is-paused');
        }
      }

      _getTouchDist(t1, t2) {
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      _onTouchStart(e) {
        if (e.touches.length === 2) {
          e.preventDefault();
          this._isPinching        = true;
          this._pinchStartDist    = this._getTouchDist(e.touches[0], e.touches[1]);
          this._pinchStartScale   = this.isZoomed ? this.zoomScale : 1;
          this._pinchCurrentScale = this._pinchStartScale;

          const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          const rect = this.imageContainer.getBoundingClientRect();
          this._pinchOriginX = ((mx - rect.left) / rect.width)  * 100;
          this._pinchOriginY = ((my - rect.top)  / rect.height) * 100;
        } else if (e.touches.length === 1) {
          this._touchStartX = e.touches[0].clientX;
          this._touchStartY = e.touches[0].clientY;
          this._panLastX    = e.touches[0].clientX;
          this._panLastY    = e.touches[0].clientY;
        }
      }

      _onTouchMove(e) {
        if (e.touches.length === 2 && this._isPinching) {
          e.preventDefault();
          if (this.imageContainer.classList.contains('video-active')) return;

          const dist  = this._getTouchDist(e.touches[0], e.touches[1]);
          const scale = Math.min(Math.max((dist / this._pinchStartDist) * this._pinchStartScale, 1), 5);
          this._pinchCurrentScale = scale;

          this.imageEl.classList.add('pan-active');
          this.imageEl.style.transformOrigin = this._pinchOriginX + '% ' + this._pinchOriginY + '%';
          this.imageEl.style.transform = scale > 1 ? 'scale(' + scale + ')' : '';
        } else if (e.touches.length === 1 && this.isZoomed && !this.imageContainer.classList.contains('video-active')) {
          e.preventDefault();
          const rect   = this.imageContainer.getBoundingClientRect();
          // Delta-based pan: subtract movement so finger drag direction matches image movement
          const deltaX = (e.touches[0].clientX - this._panLastX) / rect.width  * 100;
          const deltaY = (e.touches[0].clientY - this._panLastY) / rect.height * 100;
          this._panLastX = e.touches[0].clientX;
          this._panLastY = e.touches[0].clientY;
          const newX = Math.max(0, Math.min(100, this._currentOriginX - deltaX));
          const newY = Math.max(0, Math.min(100, this._currentOriginY - deltaY));
          this._setZoomOrigin(newX, newY, false);
        }
      }

      _onTouchEnd(e) {
        this._lastTouchEndTime = Date.now();

        if (this._isPinching && e.touches.length < 2) {
          this._isPinching = false;
          this.imageEl.classList.remove('pan-active');

          if (this._pinchCurrentScale > 1.15) {
            this.isZoomed  = true;
            this.zoomScale = this._pinchCurrentScale;
            this.imageContainer.classList.add('is-zoomed');
          } else {
            this._resetZoom();
          }
          this._pinchCurrentScale = 1;
          return;
        }

        if (!e.changedTouches.length) return;

        const x   = e.changedTouches[0].clientX;
        const y   = e.changedTouches[0].clientY;
        const now = Date.now();

        if (this.imageContainer.classList.contains('video-active')) {
          const dx = x - this._touchStartX;
          const dy = y - this._touchStartY;
          if (Math.abs(dx) < 15 && Math.abs(dy) < 15) {
            this._toggleVideo();
            return;
          }
        }

        if (!this.imageContainer.classList.contains('video-active')) {
          const timeDiff = now - this._lastTapTime;
          const tapDist  = Math.sqrt(
            Math.pow(x - this._lastTapX, 2) + Math.pow(y - this._lastTapY, 2)
          );

          if (timeDiff < 300 && tapDist < 50) {
            if (this.isZoomed) {
              this._resetZoom();
            } else {
              const rect = this.imageContainer.getBoundingClientRect();
              const px   = ((x - rect.left) / rect.width)  * 100;
              const py   = ((y - rect.top)  / rect.height) * 100;
              this.zoomScale = 2.5;
              this._zoomTo(px, py, true);
            }
            this._lastTapTime = 0;
            return;
          }

          this._lastTapTime = now;
          this._lastTapX    = x;
          this._lastTapY    = y;
        }

        if (this.isZoomed) return;
        const dx = x - this._touchStartX;
        const dy = y - this._touchStartY;

        if (dy > 80 && Math.abs(dy) > Math.abs(dx)) {
          this.close();
          return;
        }

        if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
        if (dx < 0) this.goTo(this.currentIndex + 1);
        else        this.goTo(this.currentIndex - 1);
      }
    }
  );
}

// Patch window.filterSwiperSlides once so all magnifier instances are notified
// when variant slides are rebuilt. Runs after the main inline <script> defines it.
if (!window._magnifierPatchedFilterSlides && typeof window.filterSwiperSlides === 'function') {
  window._magnifierPatchedFilterSlides = true;
  var _origFilter = window.filterSwiperSlides;
  window.filterSwiperSlides = function () {
    _origFilter.apply(this, arguments);
    setTimeout(function () {
      document.dispatchEvent(new CustomEvent('product:magnifier-slides-updated'));
    }, 80);
  };
}